import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import {
  Upload,
  Download,
  RotateCw,
  Move,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Save,
  X,
  AlertCircle,
  Image as ImageIcon,
  Sliders,
  RefreshCw,
  Check,
  Ruler,
  Lock,
  Unlock,
  AspectRatio,
  Minimize2,
  Crop,
  Square,
  MousePointer,
  Undo,
  Redo,
  Eye,
  EyeOff,
  Package,
  Filter,
  Palette,
  Settings,
  FileImage,
  Layers,
} from "lucide-react";

// Debounce hook pour optimiser les performances
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

const AdminPhotoEditor = () => {
  // États principaux
  const [image, setImage] = useState(null);
  const [originalImage, setOriginalImage] = useState(null);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [imageScale, setImageScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [compression, setCompression] = useState(0.92);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [showGrid, setShowGrid] = useState(true);
  const [loading, setLoading] = useState(false);
  const [savedImages, setSavedImages] = useState([]);

  // États pour le redimensionnement
  const [imageDimensions, setImageDimensions] = useState({
    width: 0,
    height: 0,
  });
  const [customWidth, setCustomWidth] = useState("");
  const [customHeight, setCustomHeight] = useState("");
  const [maintainAspectRatio, setMaintainAspectRatio] = useState(true);
  const [originalAspectRatio, setOriginalAspectRatio] = useState(1);

  // États pour la sélection/recadrage
  const [mode, setMode] = useState("move"); // 'move' ou 'crop'
  const [cropRect, setCropRect] = useState(null);
  const [isCropping, setIsCropping] = useState(false);
  const [cropStart, setCropStart] = useState({ x: 0, y: 0 });
  const [isDraggingCrop, setIsDraggingCrop] = useState(false);
  const [draggedHandle, setDraggedHandle] = useState(null);

  // États pour les nouvelles fonctionnalités
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [selectedFormat, setSelectedFormat] = useState("jpeg");
  const [showComparison, setShowComparison] = useState(false);
  const [filters, setFilters] = useState({
    brightness: 100,
    contrast: 100,
    saturation: 100,
    blur: 0,
  });
  const [exportSizes, setExportSizes] = useState([
    { name: "Thumbnail", width: 150, height: 150, enabled: true },
    { name: "Small", width: 300, height: 300, enabled: true },
    { name: "Medium", width: 600, height: 600, enabled: true },
    { name: "Large", width: 800, height: 800, enabled: true },
    { name: "Original", width: null, height: null, enabled: false },
  ]);
  const [previewImage, setPreviewImage] = useState(null);
  const [batchFiles, setBatchFiles] = useState([]);
  const [processingBatch, setProcessingBatch] = useState(false);
  const [customPresets, setCustomPresets] = useState([]);

  // Refs
  const canvasRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const containerRef = useRef(null);

  // Constantes
  const CANVAS_WIDTH = 800;
  const CANVAS_HEIGHT = 800;
  const MAX_ZOOM = 5;
  const MIN_ZOOM = 0.1;
  const ZOOM_STEP = 0.1;
  const MIN_CROP_SIZE = 50;
  const MAX_HISTORY = 20;

  // Valeurs debouncées pour la performance
  const debouncedFilters = useDebounce(filters, 50);
  const debouncedCompression = useDebounce(compression, 100);

  // Formats d'export disponibles
  const EXPORT_FORMATS = [
    { value: "jpeg", label: "JPEG", mimeType: "image/jpeg", extension: ".jpg" },
    {
      value: "webp",
      label: "WebP",
      mimeType: "image/webp",
      extension: ".webp",
    },
    { value: "png", label: "PNG", mimeType: "image/png", extension: ".png" },
    {
      value: "avif",
      label: "AVIF",
      mimeType: "image/avif",
      extension: ".avif",
    },
  ];

  // Presets de tailles communes
  const SIZE_PRESETS = [
    { name: "Original", value: "original" },
    { name: "Carré 800×800", value: "800x800" },
    { name: "Portrait 600×800", value: "600x800" },
    { name: "Paysage 800×600", value: "800x600" },
    { name: "Miniature 400×400", value: "400x400" },
    { name: "Instagram 1080×1080", value: "1080x1080" },
  ];

  // Calculer la qualité de compression
  const getCompressionQuality = (value) => {
    if (value >= 0.95)
      return { label: "Excellence", color: "#28a745", icon: "🏆" };
    if (value >= 0.85)
      return { label: "Très bon", color: "#5cb85c", icon: "✨" };
    if (value >= 0.7) return { label: "Bon", color: "#f0ad4e", icon: "👍" };
    if (value >= 0.5) return { label: "Moyen", color: "#ff8c00", icon: "⚡" };
    return { label: "Faible", color: "#d9534f", icon: "⚠️" };
  };

  // Sauvegarder l'état dans l'historique
  const saveToHistory = useCallback(() => {
    if (!image) return;

    const state = {
      image: image.src,
      position: { ...imagePosition },
      scale: imageScale,
      rotation: rotation,
      filters: { ...filters },
      timestamp: Date.now(),
    };

    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(state);

    if (newHistory.length > MAX_HISTORY) {
      newHistory.shift();
    }

    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  }, [
    image,
    imagePosition,
    imageScale,
    rotation,
    filters,
    history,
    historyIndex,
  ]);

  // Undo
  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      const prevState = history[historyIndex - 1];
      const img = new Image();
      img.onload = () => {
        setImage(img);
        setImagePosition(prevState.position);
        setImageScale(prevState.scale);
        setRotation(prevState.rotation);
        setFilters(prevState.filters);
        setHistoryIndex(historyIndex - 1);
      };
      img.src = prevState.image;
    }
  }, [history, historyIndex]);

  // Redo
  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const nextState = history[historyIndex + 1];
      const img = new Image();
      img.onload = () => {
        setImage(img);
        setImagePosition(nextState.position);
        setImageScale(nextState.scale);
        setRotation(nextState.rotation);
        setFilters(nextState.filters);
        setHistoryIndex(historyIndex + 1);
      };
      img.src = nextState.image;
    }
  }, [history, historyIndex]);

  // Calculer les dimensions réelles de l'image après transformation
  const calculateActualDimensions = useCallback(() => {
    if (!image) return { width: 0, height: 0 };

    const scaledWidth = image.width * imageScale;
    const scaledHeight = image.height * imageScale;

    // Calculer les dimensions après rotation
    const radians = (Math.abs(rotation) * Math.PI) / 180;
    const sin = Math.sin(radians);
    const cos = Math.cos(radians);

    const rotatedWidth =
      Math.abs(scaledWidth * cos) + Math.abs(scaledHeight * sin);
    const rotatedHeight =
      Math.abs(scaledWidth * sin) + Math.abs(scaledHeight * cos);

    return {
      width: Math.round(rotatedWidth),
      height: Math.round(rotatedHeight),
    };
  }, [image, imageScale, rotation]);

  // Charger une image
  const handleImageUpload = (e) => {
    const file = e.target ? e.target.files[0] : e;
    if (!file || !file.type.startsWith("image/")) return;

    setLoading(true);
    const reader = new FileReader();

    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        setOriginalImage(img);
        setImage(img);

        // Calculer l'échelle initiale pour que l'image rentre dans le canvas
        const scaleX = CANVAS_WIDTH / img.width;
        const scaleY = CANVAS_HEIGHT / img.height;
        const initialScale = Math.min(scaleX, scaleY, 1);

        setImageScale(initialScale);
        setImagePosition({ x: 0, y: 0 });
        setRotation(0);
        setFilters({
          brightness: 100,
          contrast: 100,
          saturation: 100,
          blur: 0,
        });
        setLoading(false);
        setCropRect(null);
        setMode("move");

        // Définir les dimensions et le ratio
        setImageDimensions({ width: img.width, height: img.height });
        setCustomWidth(img.width.toString());
        setCustomHeight(img.height.toString());
        setOriginalAspectRatio(img.width / img.height);

        // Réinitialiser l'historique
        setHistory([]);
        setHistoryIndex(-1);

        // Dessiner immédiatement
        setTimeout(() => drawCanvas(img, { x: 0, y: 0 }, initialScale, 0), 0);
      };
      img.onerror = () => {
        setLoading(false);
        alert("Erreur lors du chargement de l'image");
      };
      img.src = event.target.result;
    };

    reader.onerror = () => {
      setLoading(false);
      alert("Erreur lors de la lecture du fichier");
    };

    reader.readAsDataURL(file);
  };

  // Appliquer les filtres CSS
  const getFilterString = useCallback(() => {
    return `brightness(${filters.brightness}%) contrast(${filters.contrast}%) saturate(${filters.saturation}%) blur(${filters.blur}px)`;
  }, [filters]);

  // Dessiner sur le canvas
  const drawCanvas = useCallback(
    (img, pos, scale, rot, targetCanvas = null) => {
      const canvas = targetCanvas || canvasRef.current;
      if (!canvas || !img) return;

      const ctx = canvas.getContext("2d");

      // Effacer le canvas
      ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

      // Fond blanc
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

      // Dessiner la grille si activée et pas de canvas cible
      if (showGrid && mode === "move" && !targetCanvas) {
        ctx.strokeStyle = "rgba(200, 200, 200, 0.3)";
        ctx.lineWidth = 1;

        // Grille principale
        for (let i = 0; i <= CANVAS_WIDTH; i += 50) {
          ctx.beginPath();
          ctx.moveTo(i, 0);
          ctx.lineTo(i, CANVAS_HEIGHT);
          ctx.stroke();
        }

        for (let i = 0; i <= CANVAS_HEIGHT; i += 50) {
          ctx.beginPath();
          ctx.moveTo(0, i);
          ctx.lineTo(CANVAS_WIDTH, i);
          ctx.stroke();
        }

        // Lignes de tiers (règle des tiers)
        ctx.strokeStyle = "rgba(255, 100, 100, 0.3)";
        ctx.lineWidth = 2;

        ctx.beginPath();
        ctx.moveTo(CANVAS_WIDTH / 3, 0);
        ctx.lineTo(CANVAS_WIDTH / 3, CANVAS_HEIGHT);
        ctx.moveTo((2 * CANVAS_WIDTH) / 3, 0);
        ctx.lineTo((2 * CANVAS_WIDTH) / 3, CANVAS_HEIGHT);
        ctx.moveTo(0, CANVAS_HEIGHT / 3);
        ctx.lineTo(CANVAS_WIDTH, CANVAS_HEIGHT / 3);
        ctx.moveTo(0, (2 * CANVAS_HEIGHT) / 3);
        ctx.lineTo(CANVAS_WIDTH, (2 * CANVAS_HEIGHT) / 3);
        ctx.stroke();
      }

      // Sauvegarder le contexte
      ctx.save();

      // Appliquer les filtres
      ctx.filter = getFilterString();

      // Appliquer les transformations
      ctx.translate(CANVAS_WIDTH / 2 + pos.x, CANVAS_HEIGHT / 2 + pos.y);
      ctx.rotate((rot * Math.PI) / 180);
      ctx.scale(scale, scale);

      // Dessiner l'image centrée
      ctx.drawImage(img, -img.width / 2, -img.height / 2);

      // Restaurer le contexte
      ctx.restore();

      // Dessiner la zone de recadrage si en mode crop et pas de canvas cible
      if (mode === "crop" && cropRect && !targetCanvas) {
        // Assombrir la zone en dehors de la sélection
        ctx.fillStyle = "rgba(0, 0, 0, 0.5)";
        ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

        // Effacer la zone de sélection pour la rendre visible
        ctx.clearRect(cropRect.x, cropRect.y, cropRect.width, cropRect.height);

        // Redessiner l'image dans la zone de sélection
        ctx.save();
        ctx.beginPath();
        ctx.rect(cropRect.x, cropRect.y, cropRect.width, cropRect.height);
        ctx.clip();

        ctx.filter = getFilterString();
        ctx.translate(CANVAS_WIDTH / 2 + pos.x, CANVAS_HEIGHT / 2 + pos.y);
        ctx.rotate((rot * Math.PI) / 180);
        ctx.scale(scale, scale);
        ctx.drawImage(img, -img.width / 2, -img.height / 2);
        ctx.restore();

        // Dessiner le cadre de sélection
        ctx.strokeStyle = "#007bff";
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 5]);
        ctx.strokeRect(cropRect.x, cropRect.y, cropRect.width, cropRect.height);
        ctx.setLineDash([]);

        // Dessiner les poignées de redimensionnement
        const handleSize = 8;
        const handles = [
          { x: cropRect.x, y: cropRect.y, cursor: "nw-resize" },
          {
            x: cropRect.x + cropRect.width,
            y: cropRect.y,
            cursor: "ne-resize",
          },
          {
            x: cropRect.x,
            y: cropRect.y + cropRect.height,
            cursor: "sw-resize",
          },
          {
            x: cropRect.x + cropRect.width,
            y: cropRect.y + cropRect.height,
            cursor: "se-resize",
          },
        ];

        ctx.fillStyle = "#007bff";
        handles.forEach((handle) => {
          ctx.fillRect(
            handle.x - handleSize / 2,
            handle.y - handleSize / 2,
            handleSize,
            handleSize
          );
        });

        // Afficher les dimensions de la sélection
        ctx.fillStyle = "#007bff";
        ctx.font = "12px Arial";
        ctx.fillText(
          `${Math.round(cropRect.width)}×${Math.round(cropRect.height)}px`,
          cropRect.x + 5,
          cropRect.y - 5
        );
      }
    },
    [showGrid, mode, getFilterString]
  );

  // Effet pour redessiner quand les paramètres changent
  useEffect(() => {
    if (image && canvasRef.current) {
      drawCanvas(image, imagePosition, imageScale, rotation);
    }
  }, [
    image,
    imagePosition,
    imageScale,
    rotation,
    cropRect,
    mode,
    debouncedFilters,
    drawCanvas,
  ]);

  // Générer preview de compression
  useEffect(() => {
    if (!image || !previewCanvasRef.current) return;

    const generatePreview = async () => {
      const canvas = previewCanvasRef.current;
      canvas.width = 200;
      canvas.height = 200;

      const ctx = canvas.getContext("2d");
      const scale = Math.min(200 / image.width, 200 / image.height);

      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, 200, 200);

      ctx.save();
      ctx.filter = getFilterString();
      ctx.translate(100, 100);
      ctx.scale(scale, scale);
      ctx.drawImage(image, -image.width / 2, -image.height / 2);
      ctx.restore();

      canvas.toBlob(
        (blob) => {
          if (blob) {
            const url = URL.createObjectURL(blob);
            setPreviewImage(url);
          }
        },
        `image/${selectedFormat}`,
        debouncedCompression
      );
    };

    generatePreview();

    return () => {
      if (previewImage) URL.revokeObjectURL(previewImage);
    };
  }, [image, debouncedCompression, selectedFormat, filters, getFilterString]);

  // Mettre à jour les dimensions affichées
  useEffect(() => {
    const dims = calculateActualDimensions();
    setImageDimensions(dims);
  }, [calculateActualDimensions]);

  // Nettoyage des URLs d'objets lors du démontage
  useEffect(() => {
    return () => {
      savedImages.forEach((img) => {
        if (img.url) URL.revokeObjectURL(img.url);
      });
    };
  }, []);

  // Gestion du clic/drag selon le mode
  const handleMouseDown = (e) => {
    if (!image) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    if (mode === "move") {
      setIsDragging(true);
      setDragStart({
        x: x - imagePosition.x,
        y: y - imagePosition.y,
      });
    } else if (mode === "crop") {
      // Vérifier si on clique sur une poignée
      if (cropRect) {
        const handleSize = 16;
        const handles = [
          { x: cropRect.x, y: cropRect.y, type: "tl" },
          { x: cropRect.x + cropRect.width, y: cropRect.y, type: "tr" },
          { x: cropRect.x, y: cropRect.y + cropRect.height, type: "bl" },
          {
            x: cropRect.x + cropRect.width,
            y: cropRect.y + cropRect.height,
            type: "br",
          },
        ];

        for (let handle of handles) {
          if (
            Math.abs(x - handle.x) < handleSize &&
            Math.abs(y - handle.y) < handleSize
          ) {
            setDraggedHandle(handle.type);
            setCropStart({ x, y });
            return;
          }
        }

        // Vérifier si on clique dans la zone de recadrage pour la déplacer
        if (
          x >= cropRect.x &&
          x <= cropRect.x + cropRect.width &&
          y >= cropRect.y &&
          y <= cropRect.y + cropRect.height
        ) {
          setIsDraggingCrop(true);
          setCropStart({ x: x - cropRect.x, y: y - cropRect.y });
          return;
        }
      }

      // Commencer une nouvelle sélection
      setIsCropping(true);
      setCropStart({ x, y });
      setCropRect({ x, y, width: 0, height: 0 });
    }

    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!canvasRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    if (mode === "move" && isDragging && image) {
      const newX = x - dragStart.x;
      const newY = y - dragStart.y;
      setImagePosition({ x: newX, y: newY });
    } else if (mode === "crop") {
      if (isCropping) {
        // Créer une nouvelle sélection carrée
        const width = x - cropStart.x;
        const height = y - cropStart.y;
        const size = Math.max(Math.abs(width), Math.abs(height));

        setCropRect({
          x: width >= 0 ? cropStart.x : cropStart.x - size,
          y: height >= 0 ? cropStart.y : cropStart.y - size,
          width: size,
          height: size,
        });
      } else if (isDraggingCrop && cropRect) {
        // Déplacer la sélection
        const newX = Math.max(
          0,
          Math.min(CANVAS_WIDTH - cropRect.width, x - cropStart.x)
        );
        const newY = Math.max(
          0,
          Math.min(CANVAS_HEIGHT - cropRect.height, y - cropStart.y)
        );
        setCropRect({ ...cropRect, x: newX, y: newY });
      } else if (draggedHandle && cropRect) {
        // Redimensionner la sélection
        let newRect = { ...cropRect };
        const dx = x - cropStart.x;
        const dy = y - cropStart.y;
        const size = Math.max(
          MIN_CROP_SIZE,
          Math.max(Math.abs(dx), Math.abs(dy))
        );

        switch (draggedHandle) {
          case "tl":
            newRect.x = cropRect.x + cropRect.width - size;
            newRect.y = cropRect.y + cropRect.height - size;
            newRect.width = size;
            newRect.height = size;
            break;
          case "tr":
            newRect.y = cropRect.y + cropRect.height - size;
            newRect.width = size;
            newRect.height = size;
            break;
          case "bl":
            newRect.x = cropRect.x + cropRect.width - size;
            newRect.width = size;
            newRect.height = size;
            break;
          case "br":
            newRect.width = size;
            newRect.height = size;
            break;
        }

        // Garder dans les limites du canvas
        newRect.x = Math.max(0, newRect.x);
        newRect.y = Math.max(0, newRect.y);
        newRect.width = Math.min(CANVAS_WIDTH - newRect.x, newRect.width);
        newRect.height = Math.min(CANVAS_HEIGHT - newRect.y, newRect.height);

        setCropRect(newRect);
        setCropStart({ x, y });
      }
    }
  };

  const handleMouseUp = () => {
    if (isDragging || isCropping || isDraggingCrop || draggedHandle) {
      saveToHistory();
    }
    setIsDragging(false);
    setIsCropping(false);
    setIsDraggingCrop(false);
    setDraggedHandle(null);
  };

  // Appliquer le recadrage
  const applyCrop = () => {
    if (!cropRect || !image || !canvasRef.current) return;

    // Créer un canvas temporaire pour le recadrage
    const tempCanvas = document.createElement("canvas");
    tempCanvas.width = cropRect.width;
    tempCanvas.height = cropRect.height;
    const tempCtx = tempCanvas.getContext("2d");

    // Dessiner la partie recadrée
    tempCtx.drawImage(
      canvasRef.current,
      cropRect.x,
      cropRect.y,
      cropRect.width,
      cropRect.height,
      0,
      0,
      cropRect.width,
      cropRect.height
    );

    // Créer une nouvelle image à partir du recadrage
    const newImg = new Image();
    newImg.onload = () => {
      setImage(newImg);
      setOriginalImage(newImg);

      // Réinitialiser l'échelle et la position
      const scaleX = CANVAS_WIDTH / newImg.width;
      const scaleY = CANVAS_HEIGHT / newImg.height;
      const initialScale = Math.min(scaleX, scaleY, 1);

      setImageScale(initialScale);
      setImagePosition({ x: 0, y: 0 });
      setRotation(0);
      setCropRect(null);
      setMode("move");

      // Mettre à jour les dimensions
      setImageDimensions({ width: newImg.width, height: newImg.height });
      setCustomWidth(newImg.width.toString());
      setCustomHeight(newImg.height.toString());
      setOriginalAspectRatio(1);

      saveToHistory();
    };
    newImg.src = tempCanvas.toDataURL();
  };

  // Créer une sélection prédéfinie
  const createPresetCrop = (size) => {
    if (!image) return;

    const centerX = (CANVAS_WIDTH - size) / 2;
    const centerY = (CANVAS_HEIGHT - size) / 2;

    setCropRect({
      x: centerX,
      y: centerY,
      width: size,
      height: size,
    });
    setMode("crop");
  };

  // Gestion du zoom avec la molette
  const handleWheel = (e) => {
    if (!image || mode === "crop") return;
    e.preventDefault();

    const rect = canvasRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    // Calculer le nouveau zoom
    const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
    const newScale = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, imageScale + delta));

    // Zoom centré sur la souris
    const scaleFactor = newScale / imageScale;
    const newX = mouseX - (mouseX - imagePosition.x) * scaleFactor;
    const newY = mouseY - (mouseY - imagePosition.y) * scaleFactor;

    setImageScale(newScale);
    setImagePosition({ x: newX, y: newY });
  };

  // Fonctions de contrôle
  const handleZoomIn = () => {
    setImageScale((prev) => Math.min(MAX_ZOOM, prev + ZOOM_STEP));
  };

  const handleZoomOut = () => {
    setImageScale((prev) => Math.max(MIN_ZOOM, prev - ZOOM_STEP));
  };

  const handleRotate = (degrees) => {
    setRotation((prev) => {
      let newRotation = prev + degrees;
      // Normaliser entre -180 et 180
      while (newRotation > 180) newRotation -= 360;
      while (newRotation < -180) newRotation += 360;
      return newRotation;
    });
  };

  // Gérer le changement de dimensions personnalisées
  const handleWidthChange = (value) => {
    const width = parseInt(value) || 0;
    setCustomWidth(value);

    if (maintainAspectRatio && width > 0 && originalImage) {
      const height = Math.round(width / originalAspectRatio);
      setCustomHeight(height.toString());
      const newScale = width / originalImage.width;
      setImageScale(newScale);
    } else if (width > 0 && originalImage) {
      const newScale = width / originalImage.width;
      setImageScale(newScale);
    }
  };

  const handleHeightChange = (value) => {
    const height = parseInt(value) || 0;
    setCustomHeight(value);

    if (maintainAspectRatio && height > 0 && originalImage) {
      const width = Math.round(height * originalAspectRatio);
      setCustomWidth(width.toString());
      const newScale = height / originalImage.height;
      setImageScale(newScale);
    } else if (height > 0 && originalImage) {
      const newScale = height / originalImage.height;
      setImageScale(newScale);
    }
  };

  // Appliquer un preset de taille
  const applyPreset = (preset) => {
    if (!originalImage) return;

    if (preset === "original") {
      setImageScale(1);
      setCustomWidth(originalImage.width.toString());
      setCustomHeight(originalImage.height.toString());
    } else {
      const [width, height] = preset.split("x").map(Number);
      const scaleX = width / originalImage.width;
      const scaleY = height / originalImage.height;

      if (maintainAspectRatio) {
        const scale = Math.min(scaleX, scaleY);
        setImageScale(scale);
        setCustomWidth(Math.round(originalImage.width * scale).toString());
        setCustomHeight(Math.round(originalImage.height * scale).toString());
      } else {
        const scale = (scaleX + scaleY) / 2;
        setImageScale(scale);
        setCustomWidth(width.toString());
        setCustomHeight(height.toString());
      }
    }
  };

  const handleResetTransform = () => {
    if (!image) return;

    const scaleX = CANVAS_WIDTH / image.width;
    const scaleY = CANVAS_HEIGHT / image.height;
    const initialScale = Math.min(scaleX, scaleY, 1);

    setImageScale(initialScale);
    setImagePosition({ x: 0, y: 0 });
    setRotation(0);
    setCustomWidth(Math.round(image.width * initialScale).toString());
    setCustomHeight(Math.round(image.height * initialScale).toString());

    saveToHistory();
  };

  const handleFitToCanvas = () => {
    if (!image) return;

    const scaleX = CANVAS_WIDTH / image.width;
    const scaleY = CANVAS_HEIGHT / image.height;
    const fitScale = Math.min(scaleX, scaleY);

    setImageScale(fitScale * 0.95);
    setImagePosition({ x: 0, y: 0 });
    setCustomWidth(Math.round(image.width * fitScale * 0.95).toString());
    setCustomHeight(Math.round(image.height * fitScale * 0.95).toString());

    saveToHistory();
  };

  // Sauvegarder l'image avec multi-export
  const handleSave = async () => {
    if (!canvasRef.current || !image) return;

    setLoading(true);

    try {
      const enabledSizes = exportSizes.filter((size) => size.enabled);
      const format = EXPORT_FORMATS.find((f) => f.value === selectedFormat);

      for (const size of enabledSizes) {
        const tempCanvas = document.createElement("canvas");
        const width = size.width || CANVAS_WIDTH;
        const height = size.height || CANVAS_HEIGHT;

        tempCanvas.width = width;
        tempCanvas.height = height;
        const tempCtx = tempCanvas.getContext("2d");

        // Calculer l'échelle pour adapter au nouveau canvas
        const scale = Math.min(width / CANVAS_WIDTH, height / CANVAS_HEIGHT);
        const offsetX = (width - CANVAS_WIDTH * scale) / 2;
        const offsetY = (height - CANVAS_HEIGHT * scale) / 2;

        tempCtx.fillStyle = "#ffffff";
        tempCtx.fillRect(0, 0, width, height);

        tempCtx.save();
        tempCtx.translate(offsetX, offsetY);
        tempCtx.scale(scale, scale);
        tempCtx.drawImage(canvasRef.current, 0, 0);
        tempCtx.restore();

        tempCanvas.toBlob(
          (blob) => {
            const url = URL.createObjectURL(blob);
            const fileSize = (blob.size / 1024).toFixed(2);

            // Ajouter à la galerie seulement pour la taille principale
            if (size.name === "Large") {
              setSavedImages((prev) =>
                [
                  {
                    id: Date.now(),
                    url: url,
                    size: fileSize,
                    compression: compression,
                    dimensions: `${width}×${height}`,
                    format: selectedFormat,
                    timestamp: new Date().toLocaleString(),
                  },
                  ...prev,
                ].slice(0, 20)
              );
            }

            // Télécharger
            const a = document.createElement("a");
            a.href = url;
            a.download = `product-image-${size.name.toLowerCase()}-${Date.now()}${
              format.extension
            }`;
            a.click();

            setTimeout(() => URL.revokeObjectURL(url), 100);
          },
          format.mimeType,
          selectedFormat === "png" ? undefined : compression
        );
      }

      setLoading(false);
    } catch (error) {
      console.error("Erreur lors de la sauvegarde:", error);
      setLoading(false);
    }
  };

  // Traitement par lot
  const handleBatchUpload = (e) => {
    const files = Array.from(e.target.files || []);
    setBatchFiles(files.filter((f) => f.type.startsWith("image/")));
  };

  const processBatch = async () => {
    if (!batchFiles.length) return;

    setProcessingBatch(true);

    for (const file of batchFiles) {
      await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const img = new Image();
          img.onload = () => {
            // Traiter l'image avec les paramètres actuels
            const tempCanvas = document.createElement("canvas");
            tempCanvas.width = CANVAS_WIDTH;
            tempCanvas.height = CANVAS_HEIGHT;

            drawCanvas(img, { x: 0, y: 0 }, imageScale, rotation, tempCanvas);

            tempCanvas.toBlob(
              (blob) => {
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `batch-${file.name}`;
                a.click();
                URL.revokeObjectURL(url);
                resolve();
              },
              "image/jpeg",
              compression
            );
          };
          img.src = e.target.result;
        };
        reader.readAsDataURL(file);
      });
    }

    setProcessingBatch(false);
    setBatchFiles([]);
  };

  // Sauvegarder un preset personnalisé
  const saveCustomPreset = () => {
    const preset = {
      id: Date.now(),
      name: prompt("Nom du preset:") || `Preset ${customPresets.length + 1}`,
      scale: imageScale,
      rotation: rotation,
      filters: { ...filters },
      compression: compression,
      format: selectedFormat,
    };

    if (preset.name) {
      setCustomPresets([...customPresets, preset]);
      localStorage.setItem(
        "photoEditorPresets",
        JSON.stringify([...customPresets, preset])
      );
    }
  };

  // Charger les presets sauvegardés
  useEffect(() => {
    const saved = localStorage.getItem("photoEditorPresets");
    if (saved) {
      setCustomPresets(JSON.parse(saved));
    }
  }, []);

  // Appliquer un preset personnalisé
  const applyCustomPreset = (preset) => {
    setImageScale(preset.scale);
    setRotation(preset.rotation);
    setFilters(preset.filters);
    setCompression(preset.compression);
    setSelectedFormat(preset.format);
    saveToHistory();
  };

  // Charger une image depuis la galerie
  const loadFromGallery = (savedImage) => {
    const img = new Image();
    img.onload = () => {
      setImage(img);
      setOriginalImage(img);
      handleResetTransform();
      setCropRect(null);
      setMode("move");
    };
    img.src = savedImage.url;
  };

  // Gestion du drag & drop de fichiers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (
      !isDragging &&
      e.dataTransfer.items &&
      e.dataTransfer.items[0].kind === "file"
    ) {
      e.dataTransfer.dropEffect = "copy";
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const files = e.dataTransfer.files;
    if (files && files[0] && files[0].type.startsWith("image/")) {
      handleImageUpload(files[0]);
    }
  };

  const quality = getCompressionQuality(compression);

  return (
    <div className="admin-photo-editor">
      <div className="editor-header mb-4">
        <h2>Éditeur Photo Produit Pro</h2>
        <p className="text-muted">
          Optimisez vos images produits avec des outils avancés (800×800px)
        </p>
      </div>

      <div className="row">
        {/* Zone principale d'édition */}
        <div className="col-lg-8">
          <div className="editor-workspace">
            {/* Barre d'outils principale */}
            <div className="toolbar-main d-flex justify-content-between align-items-center mb-3">
              <div className="d-flex gap-2">
                <button
                  className={`btn ${
                    mode === "move" ? "btn-primary" : "btn-outline-secondary"
                  }`}
                  onClick={() => {
                    setMode("move");
                    setCropRect(null);
                  }}
                  disabled={!image}
                >
                  <MousePointer size={18} className="me-2" />
                  Déplacer
                </button>
                <button
                  className={`btn ${
                    mode === "crop" ? "btn-primary" : "btn-outline-secondary"
                  }`}
                  onClick={() => setMode("crop")}
                  disabled={!image}
                >
                  <Square size={18} className="me-2" />
                  Sélection carrée
                </button>
                {mode === "crop" && cropRect && (
                  <button className="btn btn-success" onClick={applyCrop}>
                    <Check size={18} className="me-2" />
                    Appliquer le recadrage
                  </button>
                )}
              </div>

              <div className="d-flex gap-2">
                <button
                  className="btn btn-outline-secondary"
                  onClick={handleUndo}
                  disabled={!image || historyIndex <= 0}
                  title="Annuler"
                >
                  <Undo size={18} />
                </button>
                <button
                  className="btn btn-outline-secondary"
                  onClick={handleRedo}
                  disabled={!image || historyIndex >= history.length - 1}
                  title="Refaire"
                >
                  <Redo size={18} />
                </button>
                <button
                  className={`btn ${
                    showComparison ? "btn-primary" : "btn-outline-secondary"
                  }`}
                  onClick={() => setShowComparison(!showComparison)}
                  disabled={!image}
                  title="Comparer avant/après"
                >
                  {showComparison ? <Eye size={18} /> : <EyeOff size={18} />}
                </button>
              </div>
            </div>

            <div className="text-center mb-3">
              <div
                className={`canvas-container ${loading ? "loading" : ""}`}
                ref={containerRef}
                onWheel={handleWheel}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                style={{ cursor: mode === "crop" ? "crosshair" : "default" }}
              >
                <canvas
                  ref={canvasRef}
                  width={CANVAS_WIDTH}
                  height={CANVAS_HEIGHT}
                  className={`editor-canvas ${isDragging ? "dragging" : ""}`}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                  style={{
                    cursor:
                      mode === "move" && image
                        ? isDragging
                          ? "grabbing"
                          : "grab"
                        : "default",
                  }}
                />

                {showComparison && originalImage && (
                  <div className="comparison-overlay">
                    <img
                      src={originalImage.src}
                      alt="Original"
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "50%",
                        height: "100%",
                        objectFit: "contain",
                        clipPath: "inset(0 50% 0 0)",
                      }}
                    />
                    <div className="comparison-divider" />
                  </div>
                )}

                {!image && !loading && (
                  <div
                    className="canvas-placeholder"
                    onClick={() => fileInputRef.current?.click()}
                    style={{ cursor: "pointer" }}
                  >
                    <ImageIcon size={64} />
                    <p className="mt-3">
                      Glissez une image ici ou cliquez pour charger
                    </p>
                  </div>
                )}
              </div>

              {image && (
                <div className="mt-2 text-muted small">
                  <span className="me-3">
                    Dimensions: {imageDimensions.width}×{imageDimensions.height}
                    px
                  </span>
                  <span className="me-3">
                    Zoom: {(imageScale * 100).toFixed(0)}%
                  </span>
                  <span className="me-3">Rotation: {rotation}°</span>
                  <span>
                    Historique: {historyIndex + 1}/{history.length}
                  </span>
                </div>
              )}
            </div>

            {/* Contrôles */}
            <div className="editor-controls">
              {/* Upload */}
              <div className="control-group mb-3">
                <h6>
                  <Upload size={16} className="me-2" />
                  Charger une image
                </h6>
                <div className="row">
                  <div className="col-md-6">
                    <label className="small text-muted">Image unique</label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="form-control"
                      disabled={loading}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="small text-muted">
                      Traitement par lot
                    </label>
                    <div className="input-group">
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleBatchUpload}
                        className="form-control"
                        disabled={loading || processingBatch}
                      />
                      <button
                        className="btn btn-outline-secondary"
                        onClick={processBatch}
                        disabled={!batchFiles.length || processingBatch}
                      >
                        <Package size={16} className="me-1" />
                        {processingBatch
                          ? "Traitement..."
                          : `Traiter (${batchFiles.length})`}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Filtres et ajustements */}
              <div className="control-group mb-3">
                <h6>
                  <Filter size={16} className="me-2" />
                  Filtres et ajustements
                </h6>
                <div className="row g-2">
                  <div className="col-md-6">
                    <label className="small text-muted">
                      Luminosité ({filters.brightness}%)
                    </label>
                    <input
                      type="range"
                      className="form-range"
                      min="0"
                      max="200"
                      value={filters.brightness}
                      onChange={(e) =>
                        setFilters({
                          ...filters,
                          brightness: parseInt(e.target.value),
                        })
                      }
                      disabled={!image}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="small text-muted">
                      Contraste ({filters.contrast}%)
                    </label>
                    <input
                      type="range"
                      className="form-range"
                      min="0"
                      max="200"
                      value={filters.contrast}
                      onChange={(e) =>
                        setFilters({
                          ...filters,
                          contrast: parseInt(e.target.value),
                        })
                      }
                      disabled={!image}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="small text-muted">
                      Saturation ({filters.saturation}%)
                    </label>
                    <input
                      type="range"
                      className="form-range"
                      min="0"
                      max="200"
                      value={filters.saturation}
                      onChange={(e) =>
                        setFilters({
                          ...filters,
                          saturation: parseInt(e.target.value),
                        })
                      }
                      disabled={!image}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="small text-muted">
                      Flou ({filters.blur}px)
                    </label>
                    <input
                      type="range"
                      className="form-range"
                      min="0"
                      max="10"
                      step="0.1"
                      value={filters.blur}
                      onChange={(e) =>
                        setFilters({
                          ...filters,
                          blur: parseFloat(e.target.value),
                        })
                      }
                      disabled={!image}
                    />
                  </div>
                </div>
                <button
                  className="btn btn-sm btn-outline-secondary mt-2"
                  onClick={() =>
                    setFilters({
                      brightness: 100,
                      contrast: 100,
                      saturation: 100,
                      blur: 0,
                    })
                  }
                  disabled={!image}
                >
                  Réinitialiser les filtres
                </button>
              </div>

              {/* Sélection/Recadrage */}
              {mode === "crop" && (
                <div className="control-group mb-3">
                  <h6>
                    <Crop size={16} className="me-2" />
                    Sélection carrée rapide
                  </h6>
                  <div className="d-flex flex-wrap gap-2">
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => createPresetCrop(200)}
                      disabled={!image}
                    >
                      200×200
                    </button>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => createPresetCrop(400)}
                      disabled={!image}
                    >
                      400×400
                    </button>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => createPresetCrop(600)}
                      disabled={!image}
                    >
                      600×600
                    </button>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => createPresetCrop(800)}
                      disabled={!image}
                    >
                      800×800
                    </button>
                    {cropRect && (
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => setCropRect(null)}
                      >
                        <X size={16} className="me-1" />
                        Annuler
                      </button>
                    )}
                  </div>
                  {cropRect && (
                    <div className="mt-2 text-center">
                      <small className="text-muted">
                        Sélection : {Math.round(cropRect.width)}×
                        {Math.round(cropRect.height)}px
                      </small>
                    </div>
                  )}
                </div>
              )}

              {/* Redimensionnement */}
              <div className="control-group mb-3">
                <h6>
                  <Ruler size={16} className="me-2" />
                  Redimensionnement
                </h6>

                {/* Presets */}
                <div className="mb-3">
                  <small className="text-muted d-block mb-2">
                    Tailles prédéfinies :
                  </small>
                  <div className="d-flex flex-wrap gap-2">
                    {SIZE_PRESETS.map((preset) => (
                      <button
                        key={preset.value}
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => applyPreset(preset.value)}
                        disabled={!image || mode === "crop"}
                      >
                        {preset.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Dimensions personnalisées */}
                <div className="row g-2 align-items-center">
                  <div className="col">
                    <label className="small text-muted">Largeur (px)</label>
                    <input
                      type="number"
                      className="form-control"
                      value={customWidth}
                      onChange={(e) => handleWidthChange(e.target.value)}
                      disabled={!image || mode === "crop"}
                      min="1"
                      max="5000"
                    />
                  </div>
                  <div className="col-auto pt-4">
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() =>
                        setMaintainAspectRatio(!maintainAspectRatio)
                      }
                      disabled={!image || mode === "crop"}
                      title={
                        maintainAspectRatio
                          ? "Déverrouiller le ratio"
                          : "Verrouiller le ratio"
                      }
                    >
                      {maintainAspectRatio ? (
                        <Lock size={18} />
                      ) : (
                        <Unlock size={18} />
                      )}
                    </button>
                  </div>
                  <div className="col">
                    <label className="small text-muted">Hauteur (px)</label>
                    <input
                      type="number"
                      className="form-control"
                      value={customHeight}
                      onChange={(e) => handleHeightChange(e.target.value)}
                      disabled={
                        !image || maintainAspectRatio || mode === "crop"
                      }
                      min="1"
                      max="5000"
                    />
                  </div>
                </div>

                {image && (
                  <div className="mt-2 text-center">
                    <small className="text-muted">
                      Ratio original : {originalAspectRatio.toFixed(2)}:1
                      {maintainAspectRatio && " (verrouillé)"}
                    </small>
                  </div>
                )}
              </div>

              {/* Zoom et position */}
              {mode === "move" && (
                <div className="control-group mb-3">
                  <h6>
                    <Move size={16} className="me-2" />
                    Zoom et Position
                  </h6>
                  <div className="d-flex gap-2 mb-3">
                    <button
                      className="btn btn-outline-secondary"
                      onClick={handleZoomOut}
                      disabled={!image || imageScale <= MIN_ZOOM}
                    >
                      <ZoomOut size={18} />
                    </button>
                    <input
                      type="range"
                      className="form-range flex-grow-1"
                      min={MIN_ZOOM}
                      max={MAX_ZOOM}
                      step={ZOOM_STEP}
                      value={imageScale}
                      onChange={(e) =>
                        setImageScale(parseFloat(e.target.value))
                      }
                      disabled={!image}
                    />
                    <button
                      className="btn btn-outline-secondary"
                      onClick={handleZoomIn}
                      disabled={!image || imageScale >= MAX_ZOOM}
                    >
                      <ZoomIn size={18} />
                    </button>
                  </div>
                  <div className="btn-group w-100">
                    <button
                      className="btn btn-outline-secondary"
                      onClick={handleResetTransform}
                      disabled={!image}
                    >
                      <RefreshCw size={16} className="me-2" />
                      Réinitialiser
                    </button>
                    <button
                      className="btn btn-outline-secondary"
                      onClick={handleFitToCanvas}
                      disabled={!image}
                    >
                      <Maximize2 size={16} className="me-2" />
                      Ajuster au cadre
                    </button>
                  </div>
                </div>
              )}

              {/* Rotation */}
              <div className="control-group mb-3">
                <h6>
                  <RotateCw size={16} className="me-2" />
                  Rotation (-180° à +180°)
                </h6>
                <div className="d-flex align-items-center gap-2">
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => handleRotate(-90)}
                    disabled={!image || mode === "crop"}
                  >
                    -90°
                  </button>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => handleRotate(-1)}
                    disabled={!image || mode === "crop"}
                  >
                    -1°
                  </button>
                  <input
                    type="range"
                    className="form-range flex-grow-1"
                    min="-180"
                    max="180"
                    step="1"
                    value={rotation}
                    onChange={(e) => setRotation(parseInt(e.target.value))}
                    disabled={!image || mode === "crop"}
                  />
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => handleRotate(1)}
                    disabled={!image || mode === "crop"}
                  >
                    +1°
                  </button>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => handleRotate(90)}
                    disabled={!image || mode === "crop"}
                  >
                    +90°
                  </button>
                </div>
                <div className="text-center mt-2">
                  <span className="badge bg-secondary">{rotation}°</span>
                  <button
                    className="btn btn-sm btn-link"
                    onClick={() => setRotation(0)}
                    disabled={!image || rotation === 0 || mode === "crop"}
                  >
                    Réinitialiser
                  </button>
                </div>
              </div>

              {/* Export et compression */}
              <div className="control-group mb-3">
                <h6>
                  <FileImage size={16} className="me-2" />
                  Format d'export
                </h6>
                <div className="row">
                  <div className="col-md-6">
                    <label className="small text-muted">Format</label>
                    <select
                      className="form-select"
                      value={selectedFormat}
                      onChange={(e) => setSelectedFormat(e.target.value)}
                    >
                      {EXPORT_FORMATS.map((format) => (
                        <option key={format.value} value={format.value}>
                          {format.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="small text-muted">Preview</label>
                    <div className="preview-container text-center">
                      <canvas
                        ref={previewCanvasRef}
                        width={200}
                        height={200}
                        style={{
                          maxWidth: "100%",
                          border: "1px solid #dee2e6",
                          borderRadius: "4px",
                        }}
                      />
                      {previewImage && (
                        <small className="d-block mt-1 text-muted">
                          Format: {selectedFormat.toUpperCase()}
                        </small>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Compression */}
              <div className="control-group mb-3">
                <h6>
                  <Sliders size={16} className="me-2" />
                  Qualité de compression
                </h6>
                <div className="compression-control">
                  <input
                    type="range"
                    className="form-range compression-slider"
                    min="0.1"
                    max="1"
                    step="0.01"
                    value={compression}
                    onChange={(e) => setCompression(parseFloat(e.target.value))}
                    disabled={selectedFormat === "png"}
                  />
                  <div className="d-flex justify-content-between align-items-center mt-2">
                    <span className="small text-muted">Fichier léger</span>
                    <span
                      className="badge"
                      style={{
                        backgroundColor: quality.color,
                        fontSize: "14px",
                      }}
                    >
                      {quality.icon} {quality.label} (
                      {(compression * 100).toFixed(0)}%)
                    </span>
                    <span className="small text-muted">Haute qualité</span>
                  </div>
                  {selectedFormat === "png" && (
                    <small className="text-muted">
                      PNG utilise une compression sans perte
                    </small>
                  )}
                </div>
              </div>

              {/* Tailles d'export */}
              <div className="control-group mb-3">
                <h6>
                  <Layers size={16} className="me-2" />
                  Tailles d'export multiples
                </h6>
                <div className="export-sizes">
                  {exportSizes.map((size, index) => (
                    <div key={index} className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`export-size-${index}`}
                        checked={size.enabled}
                        onChange={(e) => {
                          const newSizes = [...exportSizes];
                          newSizes[index].enabled = e.target.checked;
                          setExportSizes(newSizes);
                        }}
                      />
                      <label
                        className="form-check-label"
                        htmlFor={`export-size-${index}`}
                      >
                        {size.name}{" "}
                        {size.width && size.height
                          ? `(${size.width}×${size.height}px)`
                          : "(Taille originale)"}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Presets personnalisés */}
              <div className="control-group mb-3">
                <h6>
                  <Palette size={16} className="me-2" />
                  Presets personnalisés
                </h6>
                <div className="d-flex flex-wrap gap-2 mb-2">
                  {customPresets.map((preset) => (
                    <button
                      key={preset.id}
                      className="btn btn-sm btn-outline-info"
                      onClick={() => applyCustomPreset(preset)}
                      disabled={!image}
                    >
                      {preset.name}
                    </button>
                  ))}
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={saveCustomPreset}
                    disabled={!image}
                  >
                    <Settings size={14} className="me-1" />
                    Sauvegarder preset
                  </button>
                </div>
              </div>

              {/* Options */}
              <div className="control-group mb-3">
                <h6>Options d'affichage</h6>
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="showGrid"
                    checked={showGrid}
                    onChange={(e) => setShowGrid(e.target.checked)}
                    disabled={mode === "crop"}
                  />
                  <label className="form-check-label" htmlFor="showGrid">
                    Afficher la grille de composition
                  </label>
                </div>
              </div>

              {/* Actions */}
              <div className="action-buttons d-flex gap-2">
                <button
                  className="btn btn-primary flex-grow-1"
                  onClick={handleSave}
                  disabled={
                    !image || loading || !exportSizes.some((s) => s.enabled)
                  }
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Traitement...
                    </>
                  ) : (
                    <>
                      <Save size={18} className="me-2" />
                      Exporter ({
                        exportSizes.filter((s) => s.enabled).length
                      }{" "}
                      tailles)
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Galerie des images sauvegardées */}
        <div className="col-lg-4">
          <div className="photo-gallery">
            <div className="gallery-header">
              <h5>Images récentes</h5>
              <p className="text-muted small mb-0">
                {savedImages.length} image{savedImages.length > 1 ? "s" : ""}{" "}
                sauvegardée{savedImages.length > 1 ? "s" : ""}
              </p>
            </div>

            <div className="gallery-grid mt-3">
              {savedImages.length === 0 ? (
                <div className="empty-gallery text-center text-muted py-5">
                  <ImageIcon size={48} className="mb-3 opacity-50" />
                  <p>Aucune image sauvegardée</p>
                </div>
              ) : (
                savedImages.map((img) => (
                  <div key={img.id} className="gallery-item">
                    <img src={img.url} alt="Saved" />
                    <div className="gallery-item-overlay">
                      <button
                        className="btn btn-sm btn-light"
                        onClick={() => loadFromGallery(img)}
                        title="Charger dans l'éditeur"
                      >
                        <Upload size={14} />
                      </button>
                      <a
                        href={img.url}
                        download={`product-${img.id}.jpg`}
                        className="btn btn-sm btn-light"
                        title="Télécharger"
                      >
                        <Download size={14} />
                      </a>
                    </div>
                    <div className="gallery-item-info">
                      <small>{img.size} KB</small>
                      <small>{img.dimensions}</small>
                      <small>{img.format.toUpperCase()}</small>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Styles additionnels */}
      <style jsx>{`
        .comparison-overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
        }

        .comparison-divider {
          position: absolute;
          left: 50%;
          top: 0;
          bottom: 0;
          width: 2px;
          background: #007bff;
          box-shadow: 0 0 10px rgba(0, 123, 255, 0.5);
        }

        .comparison-divider::before {
          content: "AVANT";
          position: absolute;
          left: -50px;
          top: 10px;
          background: rgba(0, 0, 0, 0.7);
          color: white;
          padding: 2px 8px;
          border-radius: 3px;
          font-size: 12px;
        }

        .comparison-divider::after {
          content: "APRÈS";
          position: absolute;
          right: -50px;
          top: 10px;
          background: rgba(0, 0, 0, 0.7);
          color: white;
          padding: 2px 8px;
          border-radius: 3px;
          font-size: 12px;
        }

        .compression-slider {
          background: linear-gradient(
            to right,
            #d9534f 0%,
            #ff8c00 25%,
            #f0ad4e 50%,
            #5cb85c 75%,
            #28a745 100%
          );
        }

        .preview-container {
          background: #f8f9fa;
          padding: 10px;
          border-radius: 4px;
        }

        .toolbar-main {
          background: #f8f9fa;
          padding: 10px;
          border-radius: 8px;
          margin-bottom: 15px;
        }
      `}</style>
    </div>
  );
};

export default AdminPhotoEditor;
